import {TeamResource} from "../resource/TeamResource";
import {Team, ITeamEditableFields} from "../league/Team";
import {IApiTeam, IApiTeamMembership, IApiTeamMember, IApiMembership} from "../typings/apiResponse";
import {TeamMembership, TeamMember, Membership} from "../league/TeamMember";
import * as angular from "angular";

export class TeamService {

    public static $inject = ["TeamResource"];
    constructor(private TeamResource: TeamResource) {
    }

    /**
     * @param teamId
     * @returns {Promise<Team>}
     */
    public getTeam(teamId: string): angular.IPromise<Team> {
        return this.TeamResource.getTeam(teamId).then((apiTeam: IApiTeam) => {
            return new Team(apiTeam);
        });
    }

    /**
     * @returns {Promise<TeamMembership[]>}
     */
    public getUserTeams(): angular.IPromise<TeamMembership[]> {
        return this.TeamResource.getUserTeams().then((apiTeams: IApiTeamMembership[]) => {
            const teams = [];

            angular.forEach(apiTeams, (apiTeam: IApiTeamMembership) => {
                teams.push(new TeamMembership(apiTeam));
            });

            return teams;
        });
    }

    /**
     * @param teamId
     * @returns {Promise<TeamMembership>}
     */
    public getUserTeam(teamId: string): angular.IPromise<TeamMembership> {
        return this.TeamResource.getUserTeam(teamId).then((apiTeam: IApiTeamMembership) => {
            return new TeamMembership(apiTeam);
        });
    }

    /**
     * Returns a list of users in a team and their roles and permissions. If the embedGameaccount parameter is set
     * to a valid game account key the game account of all team members will be included if available.
     *
     * @param teamId
     * @param embedGameaccount
     *
     * @returns {Promise<TeamMember[]>}
     */
    public getTeamMembers(teamId: string, embedGameaccount?: string): angular.IPromise<TeamMember[]> {
        return this.TeamResource.getTeamMembers(teamId, embedGameaccount).then((apiTeamMembers: IApiTeamMember[]) => {
            const teamMembers = [];

            angular.forEach(apiTeamMembers, (apiTeamMember: IApiTeamMember) => {
                teamMembers.push(new TeamMember(apiTeamMember));
            });

            return teamMembers;
        });
    }

    /**
     * @param teamId
     * @param content
     * @returns {Promise<angular.IHttpPromise<any>>}
     */
    public setShortDescription(teamId: string, content: string): angular.IHttpPromise<any> {
        return this.TeamResource.setShortDescription(teamId, content);
    }

    /**
     * @param teamId
     * @param format
     * @returns {Promise<string>}
     */
    public getShortDescription(teamId: string, format?: string): angular.IPromise<string> {
        return this.TeamResource.getShortDescription(teamId, format);
    }

    /**
     * @param teamId
     * @param content
     * @returns {Promise<angular.IHttpPromise<any>>}
     */
    public setDescription(teamId: string, content: string): angular.IHttpPromise<any> {
        return this.TeamResource.setDescription(teamId, content);
    }

    /**
     * @param teamId
     * @param format
     * @returns {Promise<string>}
     */
    public getDescription(teamId: string, format?: string): angular.IPromise<string> {
        return this.TeamResource.getDescription(teamId, format);
    }

    /**
     * @param name
     * @param password
     * @returns {Promise<TeamMembership>}
     */
    public createTeam(name: string, password?: string): angular.IPromise<TeamMembership> {
        return this.TeamResource.createTeam(name, password).then((apiTeamMembership: IApiTeamMembership) => {
            return new TeamMembership(apiTeamMembership);
        });
    }

    /**
     * @param teamId
     * @param password
     * @returns {Promise<TeamMembership>}
     */
    public joinTeam(teamId: string, password?: string): angular.IPromise<TeamMembership> {
        return this.TeamResource.joinTeam(teamId, password).then((apiTeamMembership: IApiTeamMembership) => {
            return new TeamMembership(apiTeamMembership);
        });
    }

    /**
     * @param teamId
     * @param fields
     * @returns {Promise<TeamMembership>}
     */
    public editTeam(teamId: string, fields: ITeamEditableFields): angular.IPromise<TeamMembership> {
        return this.TeamResource.editTeam(teamId, fields).then((apiTeamMembership: IApiTeamMembership) => {
            return new TeamMembership(apiTeamMembership);
        });
    }

    /**
     * @param teamId
     * @param image
     * @returns {Promise<angular.IHttpPromiseCallbackArg<any>>}
     */
    public uploadLogo(teamId: string, image: File): angular.IPromise<angular.IHttpPromiseCallbackArg<any>> {
        return this.TeamResource.uploadLogo(teamId, image);
    }

    /**
     * @param teamId
     * @param userIdOrEmail Either an userId or an email address
     * @param domain For example: /play or /codworldleague
     * @returns {IPromise<angular.IHttpPromiseCallbackArg<any>>}
     */
    public inviteMember(teamId: string, userIdOrEmail: string, domain?: string): angular.IPromise<angular.IHttpPromiseCallbackArg<any>> {
        return this.TeamResource.inviteMember(teamId, userIdOrEmail, domain);
    }

    /**
     * @param teamId
     * @param userId
     * @returns {Promise<ng.IHttpPromiseCallbackArg<any>>}
     */
    public kickMember(teamId: string, userId: string): angular.IPromise<angular.IHttpPromiseCallbackArg<any>> {
        return this.TeamResource.kickMember(teamId, userId);
    }

    /**
     * @param teamId
     * @param userId
     * @param role
     * @param permission
     * @returns {Promise<Membership>}
     */
    public editMembership(teamId: string, userId: string, role?: string, permission?: string): angular.IPromise<Membership> {
        return this.TeamResource.editMembership(teamId, userId, role, permission)
            .then((apiMembership: IApiMembership) => {
                return new Membership(apiMembership);
            });
    }

    /**
     * @param teamIds
     * @param fields
     * @returns {Promise<Team[]>}
     */
    public getTeams(teamIds: string[], fields?: string[]): angular.IPromise<Team[]> {
        return this.TeamResource.getTeams(teamIds, fields)
            .then((apiTeams: IApiTeam[]) => {
                return apiTeams.map(apiTeam => new Team(apiTeam));
            });
    }

    /**
     * Returns true if the user is allowed to submit match results on behalf of his team, false otherwise.
     * @param member
     * @param rosterMemberIds
     * @returns {boolean}
     */
    public canInsertResults(member: TeamMember, rosterMemberIds: number[]): boolean {
        return this.userCanInsertResults(member.getUser().getId(), member.getMembership().getPermission(), rosterMemberIds);
    }

    /**
     * Returns true if the user is allowed to submit match results on behalf of his team, false otherwise.
     * @param userId
     * @param membership
     * @param rosterMemberIds
     * @returns {boolean}
     */
    public userCanInsertResults(userId: string, membership: string, rosterMemberIds: number[]): boolean {
        const allowedPermissions = [
            Membership.PERMISSION.LEADER,
            Membership.PERMISSION.CO_LEADER,
            Membership.PERMISSION.MATCH_ORGA
        ];

        const hasTeamPermissions = allowedPermissions.indexOf(membership) !== -1;
        const isInRoster = rosterMemberIds.indexOf(parseInt(userId, 10)) !== -1;

        return isInRoster || hasTeamPermissions;
    }

    /**
     * Delete a team.
     *
     * @param teamId Id of team to delete.
     *
     * @returns Promise of the request.
     */
    public deleteTeam(teamId: string): angular.IPromise<any> {
        return this.TeamResource.deleteTeam(teamId);
    }
}
